import { useEffect, RefObject } from 'react';

interface UseScrollSyncOptions {
  sourceRef: RefObject<HTMLElement>;
  targetRef: RefObject<HTMLElement>;
  targetSelector?: string;
  direction?: 'horizontal' | 'vertical' | 'both';
}

export const useScrollSync = ({
  sourceRef,
  targetRef,
  targetSelector,
  direction = 'horizontal',
}: UseScrollSyncOptions) => {
  useEffect(() => {
    const sourceElement = sourceRef.current;
    const targetContainer = targetRef.current;
    const targetElement = targetSelector
      ? (targetContainer?.querySelector(targetSelector) as HTMLElement | null)
      : targetContainer;

    if (!sourceElement || !targetElement) {
      return;
    }

    let scrollingSource: 'source' | 'target' | null = null;

    const syncScroll = (from: HTMLElement, to: HTMLElement) => {
      if (direction === 'horizontal' || direction === 'both') {
        to.scrollLeft = from.scrollLeft;
      }
      if (direction === 'vertical' || direction === 'both') {
        to.scrollTop = from.scrollTop;
      }
    };

    const handleSourceScroll = () => {
      if (scrollingSource !== 'target') {
        scrollingSource = 'source';
        syncScroll(sourceElement, targetElement);
      }
    };

    const handleTargetScroll = () => {
      if (scrollingSource !== 'source') {
        scrollingSource = 'target';
        syncScroll(targetElement, sourceElement);
      }
    };

    const resetScrollingSource = () => {
      scrollingSource = null;
    };

    sourceElement.addEventListener('scroll', handleSourceScroll);
    targetElement.addEventListener('scroll', handleTargetScroll);
    sourceElement.addEventListener('scrollend', resetScrollingSource, { passive: true });
    targetElement.addEventListener('scrollend', resetScrollingSource, { passive: true });

    return () => {
      sourceElement.removeEventListener('scroll', handleSourceScroll);
      targetElement.removeEventListener('scroll', handleTargetScroll);
      sourceElement.removeEventListener('scrollend', resetScrollingSource);
      targetElement.removeEventListener('scrollend', resetScrollingSource);
    };
  }, [sourceRef, targetRef, targetSelector, direction]);
};
